import React, { useEffect, Fragment, useState } from 'react'
import { Dialog, Popover, Transition } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import Link from 'next/link'

import { Button } from './Button'
import { Container } from './Container'
import { NavLink } from './NavLink'
import { Logo } from './Logo'
import { User } from 'app/types/authorization'
import clsx from 'clsx'
import { useTranslation } from 'next-i18next'
import router from 'next/router'

import { CompanyLogo } from './Image'
import { CompanyProfile } from 'app/types/organization'

export const onToggleLanguageClick = (newLocale: string) => {
  const { pathname, asPath, query } = router
  router.push({ pathname, query }, asPath, { locale: newLocale })
}
interface MobileNavLinkProps {
  href: string
  children: React.ReactNode
}

function MobileNavLink({ href, children }: MobileNavLinkProps) {
  return (
    <Popover.Button as={Link} href={href} className="block w-full p-2">
      {children}
    </Popover.Button>
  )
}

interface MobileNavIconProps {
  open: boolean
}

function MobileNavIcon({ open }: MobileNavIconProps) {
  return (
    <svg
      aria-hidden="true"
      className="h-3.5 w-3.5 overflow-visible stroke-slate-700"
      fill="none"
      strokeWidth={2}
      strokeLinecap="round"
    >
      <path
        d="M0 1H14M0 7H14M0 13H14"
        className={clsx(
          'origin-center transition',
          open && 'scale-90 opacity-0'
        )}
      />
      <path
        d="M2 2L12 12M12 2L2 12"
        className={clsx(
          'origin-center transition',
          !open && 'scale-90 opacity-0'
        )}
      />
    </svg>
  )
}

function MobileNavigation() {
  const { t } = useTranslation('common')
  return (
    <Popover>
      <Popover.Button
        className="relative z-10 flex h-8 w-8 items-center justify-center [&:not(:focus-visible)]:focus:outline-none"
        aria-label={t('toggle-navigation') ?? ''}
      >
        {({ open }) => <MobileNavIcon open={open} />}
      </Popover.Button>
      <Transition.Root>
        <Transition.Child
          as={Fragment}
          enter="duration-150 ease-out"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="duration-150 ease-in"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Popover.Overlay className="fixed inset-0 bg-slate-300/50" />
        </Transition.Child>
        <Transition.Child
          as={Fragment}
          enter="duration-150 ease-out"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="duration-100 ease-in"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <Popover.Panel
            as="div"
            className="absolute inset-x-0 top-full mt-4 flex origin-top flex-col rounded-2xl bg-white p-4 text-lg tracking-tight text-slate-900 shadow-xl ring-1 ring-slate-900/5"
          >
            <MobileNavLink href="#features">{t('functionality')}</MobileNavLink>
            <MobileNavLink href="#testimonials">{t('customers')}</MobileNavLink>
            <MobileNavLink href="#faq">{t('common-questions')}</MobileNavLink>
            <hr className="m-2 border-slate-300/40" />
            <MobileNavLink href="/sign_in">{t('sign-in')}</MobileNavLink>
          </Popover.Panel>
        </Transition.Child>
      </Transition.Root>
    </Popover>
  )
}

type HeaderProps = {
  user?: User
  isVenueLandingPage?: boolean
  companyProfile?: CompanyProfile | null
  venueId?: string
  organizationId?: string | string[]
}

const Languages = [
  { name: 'Svenska 🇸🇪', locale: 'sv' },
  { name: 'English 🇺🇸', locale: 'en' },
  { name: 'Deutsch 🇩🇪', locale: 'de' },
  { name: 'Español 🇪🇸', locale: 'es' },
  { name: 'Nederlands 🇳🇱', locale: 'nl' },
  { name: 'Čeština 🇨🇿', locale: 'cs' },
  { name: 'Dansk 🇩🇰', locale: 'da' },
  { name: 'Eesti 🇪🇪', locale: 'et' },
  { name: 'Suomi 🇫🇮', locale: 'fi' },
  { name: 'Français 🇫🇷', locale: 'fr' },
  { name: 'Íslenska 🇮🇸', locale: 'is' },
  { name: 'Italiano 🇮🇹', locale: 'it' },
  { name: '日本語 🇯🇵', locale: 'ja' },
  { name: '한국어 🇰🇷', locale: 'ko' },
  { name: 'Latviešu 🇱🇻', locale: 'lt' },
  { name: 'Lithuanian 🇱🇹', locale: 'lv' },
  { name: 'Norsk 🇳🇴', locale: 'no' },
  { name: 'Português 🇵🇹', locale: 'pt' },
  { name: 'Русский', locale: 'ru' },
  { name: '官话 🇨🇳', locale: 'zh' },
  { name: 'اَلْعَرَبِيَّةُ 🇦🇪', locale: 'ar' },
]

function Header({
  user,
  isVenueLandingPage = false,
  companyProfile,
  venueId,
  organizationId,
}: HeaderProps) {
  const { t } = useTranslation('common')

  const pages = [
    { name: t('find-events'), href: '/' },
    { name: t('for-organizers'), href: '/venue/about' },
  ]

  const [open, setOpen] = useState(false)

  const [tabs, setTabs] = useState([
    {
      id: 1,
      name:
        organizationId === '2tD5nD2eFkHB8nsxOrsl3ss0SaD' ||
        organizationId === '2mecAoMkzIwN2n7iTDQ266Vrt2Q' ||
        organizationId === '2bRJOsbrW5CNEjuZcGarec7mRcw'
          ? t('start')
          : t('tickets'),
      current: false,
      onPage: true,
      href: venueId,
    },
  ])

  useEffect(() => {
    setTabs([
      {
        id: 1,
        name:
          organizationId === '2tD5nD2eFkHB8nsxOrsl3ss0SaD' ||
          organizationId === '2mecAoMkzIwN2n7iTDQ266Vrt2Q' ||
          organizationId === '2bRJOsbrW5CNEjuZcGarec7mRcw'
            ? t('start')
            : t('tickets'),
        current: false,
        onPage: true,
        href: venueId,
      },
    ])
  }, [t, organizationId, venueId])

  const handleMouseEnter = (id: number) => {
    setTabs((tabs) =>
      tabs?.map((tab) => {
        if (tab.id === id) {
          return {
            ...tab,
            current: true,
          }
        }
        return tab
      })
    )
  }

  const handleMouseLeave = (id: number) => {
    setTabs((tabs) =>
      tabs?.map((tab) => {
        if (tab.id === id) {
          return {
            ...tab,
            current: false,
          }
        }
        return tab
      })
    )
  }

  const titleStyle = {
    color: companyProfile?.primary_inverted
      ? '#000000'
      : '#FFFFFF' || 'primary200',
    borderBottom: `8px solid ${companyProfile?.secondary_main}`,
    marginBottom: '-34px',
    paddingBottom: '26px',
  }

  const defaultTitleStyle = {
    color: 'white',
    borderBottom: '0px',
    marginBottom: '0px',
    paddingBottom: '0px',
  }

  if (isVenueLandingPage) {
    return (
      <header className="py-10">
        <Container>
          <nav className="relative z-50 flex justify-between">
            <div className="flex items-center md:gap-x-12">
              <Link href="/" aria-label={t('home') ?? ''}>
                <Logo className="h-10 w-auto" />
              </Link>
              <div className="hidden md:flex md:gap-x-6">
                <NavLink href="#features">{t('functionality')}</NavLink>
                <NavLink href="#testimonials">{t('customers')}</NavLink>
                <NavLink href="#faq">{t('common-questions')}</NavLink>
              </div>
            </div>
            <div className="flex items-center gap-x-5 md:gap-x-8">
              <Button href="/contact" color="blue">
                <span>
                  {t('be-contacted')}{' '}
                  <span className="hidden lg:inline">{t('today')}</span>
                </span>
              </Button>
              <div className="-mr-1 md:hidden">
                <MobileNavigation />
              </div>
            </div>
          </nav>
        </Container>
      </header>
    )
  }

  return (
    <div className="bg-white">
      {/* Mobile menu */}
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-40 lg:hidden" onClose={setOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 z-40 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative flex w-full max-w-xs flex-col overflow-y-auto bg-white pb-12 shadow-xl">
                <div className="flex px-4 pb-2 pt-5">
                  <button
                    type="button"
                    className="-m-2 inline-flex items-center justify-center rounded-md p-2 text-gray-400"
                    onClick={() => setOpen(false)}
                  >
                    <span className="sr-only">{t('close-menu')}</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                {/* Links */}
                <div className="gap-y-6 border-t border-gray-200 px-4 py-6">
                  {pages?.map((page) => (
                    <div key={page.name} className="flow-root">
                      <Link
                        href={page.href}
                        className="-m-2 block p-2 font-medium text-gray-900"
                      >
                        {page.name}
                      </Link>
                    </div>
                  ))}
                </div>

                <div className="gap-y-6 border-t border-gray-200 px-4 py-6">
                  <div className="flow-root">
                    <a
                      href="#"
                      className="-m-2 block p-2 font-medium text-gray-900"
                    >
                      {t('create-an-account')}
                    </a>
                  </div>
                  <div className="flow-root">
                    <a
                      href="#"
                      className="-m-2 block p-2 font-medium text-gray-900"
                    >
                      {t('sign-in')}
                    </a>
                  </div>
                </div>

                <div className="gap-y-6 border-t border-gray-200 px-4 py-6" />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      <header className="relative">
        <nav aria-label={t('top') ?? ''}>
          {/* Top navigation */}

          <div
            style={{
              backgroundColor:
                companyProfile?.primary_main && companyProfile.primary_main,
            }}
          >
            <div
              style={{
                backgroundColor:
                  companyProfile?.primary_main && companyProfile.primary_main,
              }}
              className="bg-blue-700 py-2"
            >
              <div className="mx-auto flex  max-w-7xl items-center justify-between">
                <div className="ml-5 h-16 sm:ml-0">
                  {venueId !== undefined ? (
                    <a target="_self" href={`/stores/${venueId}`}>
                      <CompanyLogo
                        imageLink={companyProfile?.logo ?? undefined}
                      />
                    </a>
                  ) : (
                    <CompanyLogo
                      imageLink={companyProfile?.logo ?? undefined}
                    />
                  )}
                </div>
                <div className="font-inter ml-5 hidden h-full flex-row items-center gap-x-5 font-bold text-white sm:flex">
                  {tabs?.map((tab) => (
                    <div
                      key={tab.id}
                      style={
                        tab.onPage
                          ? titleStyle
                          : tab.current
                          ? titleStyle
                          : defaultTitleStyle
                      }
                      onMouseEnter={() => handleMouseEnter(tab.id)}
                      onMouseLeave={() => handleMouseLeave(tab.id)}
                      className={`mr-5 flex h-full items-center text-white ${
                        tab.id === 1 ? 'ml-10' : ''
                      } -z-5 overflow-hidden`}
                    >
                      {venueId !== undefined ? (
                        <Link href={`/stores/${venueId}`}>{tab.name}</Link>
                      ) : (
                        <span className="cursor-default">{tab.name}</span>
                      )}
                    </div>
                  ))}
                </div>

                <div className="mr-4 hidden flex-1 items-center justify-end gap-x-6 sm:mr-0 sm:flex">
                  {user === undefined && (
                    <Link
                      href="/sign_in"
                      className="text-sm font-bold text-white hover:text-gray-100"
                    >
                      {t('sign-in')}
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        </nav>
      </header>
    </div>
  )
}

export { Header, type HeaderProps, Languages }

// Extracted text strings
// t('toggle-navigation') - Toggle navigation
// t('functionality') - Functionality
// t('customers') - Customers
// t('common-questions') - Common questions
// t('sign-in') - Sign in
// t('swedish') - Svenska 🇸🇪
// t('english') - English 🇺🇸
// t('german') - Deutsch 🇩🇪
// t('spanish') - Español 🇪🇸
// t('dutch') - Nederlands 🇳🇱
// t('czech') - Čeština 🇨🇿
// t('danish') - Dansk 🇩🇰
// t('estonian') - Eesti 🇪🇪
// t('finnish') - Suomi 🇫🇮
// t('french') - Français 🇫🇷
// t('icelandic') - Íslenska 🇮🇸
// t('italian') - Italiano 🇮🇹
// t('japanese') - 日本語 🇯🇵
// t('korean') - 한국어 🇰🇷
// t('latvian') - Latviešu 🇱🇻
// t('lithuanian') - Lithuanian 🇱🇹
// t('norwegian') - Norsk 🇳🇴
// t('portuguese') - Português 🇵🇹
// t('russian') - Русский
// t('chinese') - 官话 🇨🇳
// t('arabic') - اَلْعَرَبِيَّةُ 🇦🇪
// t('find-events') - Find events
// t('for-organizers') - For organizers
// t('start') - Start
// t('tickets') - Tickets
// t('home') - Home
// t('be-contacted') - Be contacted
// t('today') - today
// t('close-menu') - Close menu
// t('create-an-account') - Create an account
// t('top') - Top
